import isEqual from 'lodash.isequal'

import { creditCardSchema } from 'auth/schemas'
import { pendingSelector } from 'api/selectors'
import { createDenormalizedSelector, entitiesSelector } from 'entities/selectors'
import { rateSchema } from 'grs/schemas'
import { userSelector } from 'auth/selectors'
import { basketSelector } from 'basket/selectors'
import { getRateId } from 'grs/schemas'
import { selectedEventLocationIdSelector } from 'search/selectors'
import { getTotalPrice } from 'shared/price-utilities'
import { createSelector } from 'reselect'

export const stateSelector = state => state.booking

export const selectedRateIdSelector = createSelector(stateSelector, state => state.selectedRate)
export const bookingItemSelector = createSelector(stateSelector, state => state.bookingItem)
export const selectedVehicleSelector = createSelector(stateSelector, state => state.selectedVehicles)
export const selectedCreditCardIdSelector = createSelector(stateSelector, state => state.card)
export const selectedCreditCardSelector = createDenormalizedSelector(selectedCreditCardIdSelector, creditCardSchema)

export const disabledCardsSelector = createSelector(stateSelector, state => state.disabledCards)

export const bookingRatesSelector = createSelector(stateSelector, state =>
    state.rates instanceof Array ? state.rates : []
)
export const locationDetailsSelector = createSelector(stateSelector, state => state.bookingLocationDetails)
export const locationDataSelector = createSelector(selectedRateIdSelector, state => state?.locationData)

export const ratesPendingSelector = createSelector(pendingSelector, pending => pending.bookingRates)
export const ratesErrorSelector = createSelector(
    ratesPendingSelector,
    pending => pending instanceof Error && pending.message
)
export const ratesAreLoadingSelector = createSelector(
    ratesPendingSelector,
    ratesPending => ratesPending && !(ratesPending instanceof Error)
)

export const locationDetailsPendingSelector = createSelector(pendingSelector, pending => pending.locationDetails)

export const unfilteredRatesSelector = createDenormalizedSelector(bookingRatesSelector, [rateSchema])
export const vehiclesSelector = createSelector(userSelector, user => (user ? user.vehicles : []))
export const doShowSubscriptionsSelector = (_, { showSubscriptions }) => showSubscriptions

export const selectedVehicleDetailsSelector = createSelector(
    selectedVehicleSelector,
    entitiesSelector,
    (selectedVehicles, entities) => {
        if (selectedVehicles.length === 0) return null
        if (!entities?.vehicles) return null

        const selectedVehicleId = selectedVehicles[0]?.id
        return entities?.vehicles[selectedVehicleId] || null
    }
)

export const ratesSelector = createSelector(
    unfilteredRatesSelector,
    doShowSubscriptionsSelector,
    (rates, showSubscriptions) =>
        rates.filter(rate => (rate.isMonthlyPass && showSubscriptions) || (!rate.isMonthlyPass && !showSubscriptions))
)
export const rateSelector = createDenormalizedSelector(selectedRateIdSelector, rateSchema)
export const isRateSubscriptionSelector = createSelector(rateSelector, rate => rate && rate.isMonthlyPass)

export const lastBookingDetailsSelector = createSelector(stateSelector, state => state.lastBookingDetails)
export const lastBookingBasketItemSelector = createSelector(
    lastBookingDetailsSelector,
    selectedVehicleSelector,
    (lastBookingDetails, vehicles) => {
        if (lastBookingDetails) {
            return {
                id: lastBookingDetails.id,
                rate: { ...lastBookingDetails.rate, waitingListKey: lastBookingDetails.waitingListKey },
                promoCode: lastBookingDetails.promoCode,
                vehicles,
                autoRenew: lastBookingDetails.autoRenew,
            }
        } else {
            return null
        }
    }
)

export const lastBookingBasketItemTotalPriceSelector = createSelector(
    lastBookingBasketItemSelector,
    lastBookingBasketItem => {
        if (lastBookingBasketItem) {
            return {
                amount: getTotalPrice(lastBookingBasketItem),
                currencySymbol: lastBookingBasketItem.rate.currencySymbol,
            }
        } else {
            return null
        }
    }
)

export const isSameTicketInBasketSelector = createSelector(
    lastBookingBasketItemSelector,
    basketSelector,
    (lastBookingBasketItem, basket) => {
        if (lastBookingBasketItem && basket.items.length > 0) {
            const lastItem = {
                rate: getRateId(lastBookingBasketItem.rate),
                vehicles: lastBookingBasketItem.vehicles,
            }

            if (basket.items.some(element => element.id === lastBookingBasketItem.id)) return false

            return basket.items.some(element =>
                isEqual({ rate: getRateId(element.rate), vehicles: element.vehicles }, lastItem)
            )
        }

        return false
    }
)

export const promoCodeBarcodeSelector = createSelector(lastBookingBasketItemSelector, lastBookingBasketItem => {
    if (lastBookingBasketItem && lastBookingBasketItem.promoCode) {
        return lastBookingBasketItem.promoCode.barcode
    }
    return null
})

export const rawPromoCodeSelector = createSelector(stateSelector, state => state.rawPromoCode)

export const lastBookingLinkSelector = createSelector(lastBookingDetailsSelector, details => {
    if (details) {
        const { grsId, rateId, subscriptions, waitingListKey } = details

        return {
            pathname: `/booking/${grsId}${subscriptions ? '/subscriptions' : ''}`,
            search: { rateId, waitingListKey },
        }
    }

    return null
})

export const seasonTicketsSelector = createSelector(stateSelector, ({ seasonTickets }) => seasonTickets)
export const selectedSeasonTicketSelector = createSelector(
    stateSelector,
    ({ selectedSeasonTicket }) => selectedSeasonTicket
)
export const selectedTicketRatePendingSelector = createSelector(pendingSelector, pending => pending.getSeasonTicketRate)
export const seasonTicketsPendingSelector = createSelector(pendingSelector, pending => pending.getSeasonTickets)
export const waitingListKeySelector = createSelector(stateSelector, state => state.waitingListKey)
export const waitingListPendingSelector = createSelector(
    pendingSelector,
    pending => pending.getWaitingListRate || pending.validateWaitingListKey
)
export const waitingListNextAvailableDateSelector = createSelector(
    stateSelector,
    state => state.waitingListNextAvailableDate
)
export const parkingModeSelector = createSelector(stateSelector, state => state.parkingMode)

export const eventBookingUrlSelector = createSelector(selectedEventLocationIdSelector, selectedEventLocationId =>
    selectedEventLocationId ? `/booking/${selectedEventLocationId}/events` : null
)

export const upSellOptionsSelector = createSelector(stateSelector, state => state.upSellOptions?.rateUpSellOptions)
export const selectedUpSellOptionSelector = createSelector(stateSelector, state => state.selectedUpSellOption)

export const threeDsSelector = createSelector(stateSelector, state => state.threeDs)

export const getUpSellOptionPendingSelector = createSelector(pendingSelector, pending => pending.getUpSellOptions)
export const upgradeUpSellOptionPendingSelector = createSelector(
    pendingSelector,
    pending => pending.upgradeUpSellOption
)
export const upgradeUpSellErrorSelector = createSelector(
    upgradeUpSellOptionPendingSelector,
    pending => pending instanceof Error && pending.message
)

export const additionalParkingItemsSelector = createSelector(stateSelector, state => state.additionalParkingItems)

export const qrCodeDataSelector = createSelector(stateSelector, state => state.qrCodeData)

export const addtionalItemsPendingSelector = createSelector(
    pendingSelector,
    pending => pending.GET_ADDITIONAL_PARKING_ITEMS
)

export const threeDsDataSelector = createSelector(threeDsSelector, threeDs => threeDs)

export const guestUserCardVerifiedSelector = createSelector(
    stateSelector,
    state => state.guestUserChekoutData?.guestUserCardIsVerified
)
export const guestUserPersonalDataSelector = createSelector(
    stateSelector,
    state => state.guestUserChekoutData?.guestUserPersonalData
)
